import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { API_URL, formatDateTime, getInitials, cn } from "../../lib/utils";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../components/ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

import {
    MessageSquare,
    Search,
    Plus,
    Send,
    MoreVertical,
    Users,
    Hash,
    User,
    FolderKanban,
    Check,
    CheckCheck,
    Loader2,
    ArrowLeft,
    Archive,
    Video,
    ExternalLink,
    Paperclip,
    Download,
    FileText,
    Image as ImageIcon,
    X,
    Edit,
    Trash2,
    UserPlus,
    UserMinus,
    LogOut,
    Shield,
} from "lucide-react";

const POLLING_INTERVAL = 5000;

// ── Presence status config ──
const PRESENCE_CONFIG = {
    online:  { color: "bg-emerald-500", ring: "ring-emerald-500/30" },
    away:    { color: "bg-amber-400",   ring: "ring-amber-400/30"   },
    busy:    { color: "bg-red-500",     ring: "ring-red-500/30"     },
    offline: { color: "bg-gray-400",    ring: "ring-gray-400/30"    },
};

// ── Presence Dot ──
const PresenceDot = ({ status, size = "sm", className = "" }) => {
    const cfg = PRESENCE_CONFIG[status] || PRESENCE_CONFIG.offline;
    const sizeClass = size === "lg" ? "w-3.5 h-3.5 ring-[3px]" : "w-2.5 h-2.5 ring-2";
    return (
        <span
            className={cn("rounded-full ring-background", cfg.color, cfg.ring, sizeClass, className)}
            data-testid={`presence-dot-${status}`}
        />
    );
};

// ── Conversation List Item ──
const ConversationItem = ({ conv, isActive, unread, userId, onClick, getConversationName, presenceMap }) => {
    const Icon = conv.type === "direct" ? User : conv.type === "project" ? FolderKanban : conv.type === "team" ? Users : Hash;
    const otherUser = conv.type === "direct" ? conv.participants?.find(p => p.user_id !== userId) : null;
    const otherPresence = otherUser ? (presenceMap[otherUser.user_id] || "offline") : null;

    return (
        <button
            onClick={onClick}
            className={cn(
                "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                isActive ? "bg-primary/10 text-primary" : "hover:bg-muted"
            )}
            data-testid={`conversation-${conv.conversation_id}`}
        >
            <div className="relative">
                {conv.type === "direct" ? (
                    <Avatar className="h-10 w-10">
                        <AvatarImage src={otherUser?.avatar} />
                        <AvatarFallback>{getInitials(getConversationName(conv))}</AvatarFallback>
                    </Avatar>
                ) : (
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                    </div>
                )}
                {/* Presence indicator for direct chats */}
                {otherPresence && (
                    <span className="absolute -bottom-0.5 -right-0.5">
                        <PresenceDot status={otherPresence} />
                    </span>
                )}
                {/* Unread badge (overrides presence position if both) */}
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <p className={cn("font-medium truncate text-sm", unread > 0 && "font-bold")}>
                        {getConversationName(conv)}
                    </p>
                    <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                        {conv.last_message && formatDateTime(conv.last_message.created_at)}
                    </span>
                </div>
                {conv.last_message && (
                    <p className={cn("text-xs truncate mt-0.5", unread > 0 ? "text-foreground font-medium" : "text-muted-foreground")}>
                        {conv.last_message.type === "system" || conv.last_message.type === "meeting"
                            ? conv.last_message.content
                            : `${conv.last_message.sender_name?.split(" ")[0]}: ${conv.last_message.content}`}
                    </p>
                )}
            </div>
        </button>
    );
};

// ── Meeting Message Card ──
const MeetingMessageCard = ({ msg, isOwn, t, onJoinCall }) => {
    const link = msg.meeting_link;
    return (
        <div className={cn("flex gap-2", isOwn ? "justify-end" : "justify-start")} data-testid={`message-${msg.message_id}`}>
            <div className={cn(
                "max-w-[75%] rounded-2xl overflow-hidden border",
                isOwn ? "border-primary/30 bg-primary/5" : "border-border bg-card"
            )}>
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                    <Video className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm font-medium">{msg.content}</span>
                </div>
                {link && (
                    <div className="px-4 pb-3 pt-1 flex gap-2">
                        <button
                            onClick={() => onJoinCall(link)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                            data-testid="join-video-call-link"
                        >
                            <Video className="w-3 h-3" />
                            {t("messaging.joinVideoCall")}
                        </button>
                        <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:underline"
                            data-testid="open-video-call-tab"
                        >
                            <ExternalLink className="w-3 h-3" />
                            {t("messaging.openInNewTab")}
                        </a>
                    </div>
                )}
                <div className={cn("flex items-center gap-1 px-4 pb-2 text-[10px] text-muted-foreground", isOwn ? "justify-end" : "justify-start")}>
                    <span>{msg.sender_name}</span>
                    <span>·</span>
                    <span>{formatDateTime(msg.created_at)}</span>
                </div>
            </div>
        </div>
    );
};

// ── File size formatter ──
const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Image Lightbox ──
const ImageLightbox = ({ src, alt, onClose }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="image-lightbox"
    >
        <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lightbox-close-btn"
        >
            <X className="w-5 h-5" />
        </button>
        <img
            src={src}
            alt={alt}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
        />
    </div>
);

// ── Authenticated Image ──
const AuthImage = ({ src, alt, token, className, onClick, ...props }) => {
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadImage = async () => {
            try {
                const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok || cancelled) return;
                const blob = await res.blob();
                if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
            } catch { /* silent */ }
        };
        loadImage();
        return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, token]);

    if (!blobUrl) return <div className={cn("bg-muted animate-pulse", className)} style={{ minHeight: "80px" }} />;

    return (
        <img src={blobUrl} alt={alt} className={className} onClick={() => onClick?.(blobUrl, alt)} {...props} />
    );
};

// ── File Message Card ──
const FileMessageCard = ({ msg, isOwn, token, onImageClick, t }) => {
    const file = msg.file;
    if (!file) return null;

    const isImage = file.is_image;
    const fileUrl = `${API_URL}/messaging/files/${file.file_id}`;
    const downloadUrl = `${API_URL}/files/${file.file_id}/download`;

    return (
        <div className={cn("flex gap-2", isOwn ? "justify-end" : "justify-start")} data-testid={`message-${msg.message_id}`}>
            {!isOwn && (
                <Avatar className="h-7 w-7 mt-1 shrink-0">
                    <AvatarImage src={msg.sender_avatar} />
                    <AvatarFallback className="text-[10px]">{getInitials(msg.sender_name)}</AvatarFallback>
                </Avatar>
            )}
            <div className={cn("max-w-[75%]", isOwn ? "items-end" : "items-start")}>
                {!isOwn && (
                    <p className="text-[11px] text-muted-foreground mb-0.5 ml-1">{msg.sender_name}</p>
                )}
                <div className={cn(
                    "rounded-2xl overflow-hidden border",
                    isOwn ? "border-primary/20 bg-primary/5" : "border-border bg-card"
                )}>
                    {/* Image preview */}
                    {isImage && (
                        <div className="cursor-zoom-in" data-testid={`image-preview-${file.file_id}`}>
                            <AuthImage
                                src={fileUrl}
                                alt={file.original_name}
                                token={token}
                                className="max-w-full max-h-64 object-cover"
                                style={{ minWidth: "160px" }}
                                onClick={onImageClick}
                            />
                        </div>
                    )}
                    {/* File info bar */}
                    <div className="flex items-center gap-2.5 px-3 py-2">
                        <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                            isImage ? "bg-blue-500/10" : "bg-orange-500/10"
                        )}>
                            {isImage ? (
                                <ImageIcon className="w-4 h-4 text-blue-500" />
                            ) : (
                                <FileText className="w-4 h-4 text-orange-500" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.original_name}</p>
                            <p className="text-[11px] text-muted-foreground">{formatFileSize(file.size_bytes)}</p>
                        </div>
                        <button
                            onClick={async (e) => {
                                e.preventDefault();
                                try {
                                    const res = await fetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
                                    if (!res.ok) throw new Error();
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = file.original_name;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                } catch { /* silent */ }
                            }}
                            className="shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors"
                            data-testid={`download-file-${file.file_id}`}
                        >
                            <Download className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>
                    {/* Caption if different from filename */}
                    {msg.content && msg.content !== file.original_name && (
                        <div className="px-3 pb-2">
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                    )}
                </div>
                <div className={cn("flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground", isOwn ? "justify-end" : "justify-start")}>
                    <span>{formatDateTime(msg.created_at)}</span>
                    {isOwn && (msg.read_by?.length > 1 ? <CheckCheck className="w-3 h-3 text-primary" /> : <Check className="w-3 h-3" />)}
                </div>
            </div>
        </div>
    );
};

// ── Message Bubble ──
const MessageBubble = ({ msg, isOwn, isSystem, isMeeting, isFile, showAvatar, token, onImageClick, onJoinCall, t }) => {
    if (isSystem) {
        return (
            <div className="flex justify-center">
                <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                    {msg.content}
                </span>
            </div>
        );
    }

    if (isMeeting) {
        return <MeetingMessageCard msg={msg} isOwn={isOwn} t={t} onJoinCall={onJoinCall} />;
    }

    if (isFile) {
        return <FileMessageCard msg={msg} isOwn={isOwn} token={token} onImageClick={onImageClick} t={t} />;
    }

    return (
        <div className={cn("flex gap-2", isOwn ? "justify-end" : "justify-start")} data-testid={`message-${msg.message_id}`}>
            {!isOwn && showAvatar && (
                <Avatar className="h-7 w-7 mt-1 shrink-0">
                    <AvatarImage src={msg.sender_avatar} />
                    <AvatarFallback className="text-[10px]">{getInitials(msg.sender_name)}</AvatarFallback>
                </Avatar>
            )}
            {!isOwn && !showAvatar && <div className="w-7 shrink-0" />}
            <div className={cn("max-w-[75%]", isOwn ? "items-end" : "items-start")}>
                {!isOwn && showAvatar && (
                    <p className="text-[11px] text-muted-foreground mb-0.5 ml-1">{msg.sender_name}</p>
                )}
                <div className={cn(
                    "rounded-2xl px-3.5 py-2 text-sm",
                    isOwn ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted rounded-bl-md"
                )}>
                    {msg.reply_to && (
                        <div className={cn("text-xs mb-1 pb-1 border-b", isOwn ? "border-primary-foreground/30" : "border-border")}>
                            <span className="opacity-70">&#8627; {msg.reply_to.sender_name}: </span>
                            {msg.reply_to.content}
                        </div>
                    )}
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    {msg.is_edited && <span className="text-[10px] opacity-60">({t("messaging.edited")})</span>}
                </div>
                <div className={cn("flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground", isOwn ? "justify-end" : "justify-start")}>
                    <span>{formatDateTime(msg.created_at)}</span>
                    {isOwn && (msg.read_by?.length > 1 ? <CheckCheck className="w-3 h-3 text-primary" /> : <Check className="w-3 h-3" />)}
                </div>
            </div>
        </div>
    );
};

// ── Main Page ──
export const MessagingPage = () => {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sending, setSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [users, setUsers] = useState([]);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [mobileShowChat, setMobileShowChat] = useState(false);
    const [presenceMap, setPresenceMap] = useState({});
    const [startingCall, setStartingCall] = useState(false);
    const [jitsiUrl, setJitsiUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);

    const fileInputRef = useRef(null);

    const [newConvName, setNewConvName] = useState("");
    const [newConvDesc, setNewConvDesc] = useState("");
    const [newConvType, setNewConvType] = useState("group");
    const [newConvParticipants, setNewConvParticipants] = useState([]);
    const [showGroupInfo, setShowGroupInfo] = useState(false);
    const [editingGroupName, setEditingGroupName] = useState(false);
    const [editGroupNameVal, setEditGroupNameVal] = useState("");

    const messagesEndRef = useRef(null);
    const pollingRef = useRef(null);
    const activeConvRef = useRef(null);

    useEffect(() => { activeConvRef.current = activeConversation; }, [activeConversation]);

    // ── API helpers ──
    const apiGet = useCallback(async (endpoint) => {
        const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "API error"); }
        return res.json();
    }, [token]);

    const apiPost = useCallback(async (endpoint, data) => {
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "API error"); }
        return res.json();
    }, [token]);

    const request = useCallback(async (endpoint, options = {}) => {
        const res = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...options.headers },
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "API error"); }
        return res.json();
    }, [token]);

    const apiPut = useCallback(async (endpoint, data) => {
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error("API error");
        return res.json();
    }, [token]);

    // ── Load conversations ──
    const loadConversations = useCallback(async () => {
        try {
            const data = await apiGet("/messaging/conversations");
            setConversations(data);
            const counts = {};
            data.forEach(c => { if (c.unread_count > 0) counts[c.conversation_id] = c.unread_count; });
            setUnreadCounts(counts);
        } catch (err) {
            console.error("Error loading conversations:", err);
        }
    }, [apiGet]);

    // ── Load messages ──
    const loadMessages = useCallback(async (conversationId) => {
        if (!conversationId) return;
        setLoadingMessages(true);
        try {
            const data = await apiGet(`/messaging/conversations/${conversationId}/messages`);
            setMessages(data.messages || []);
            setUnreadCounts(prev => { const next = { ...prev }; delete next[conversationId]; return next; });
        } catch (err) {
            console.error("Error loading messages:", err);
            toast.error(t("messaging.errorLoading"));
        } finally {
            setLoadingMessages(false);
        }
    }, [apiGet, t]);

    // ── Load users ──
    const loadUsers = useCallback(async () => {
        try {
            const data = await apiGet("/users");
            setUsers(data.filter(u => u.user_id !== user?.user_id));
        } catch (err) {
            console.error("Error loading users:", err);
        }
    }, [apiGet, user]);

    // ── Update presence (PUT) ──
    const updatePresence = useCallback(async () => {
        try {
            await apiPut("/messaging/presence", { status: "online" });
        } catch (err) { /* silent */ }
    }, [apiPut]);

    // ── Fetch presence for all tenant users ──
    const fetchPresence = useCallback(async () => {
        try {
            const data = await apiGet("/messaging/presence");
            const map = {};
            data.forEach(p => { map[p.user_id] = p.status; });
            setPresenceMap(map);
        } catch (err) { /* silent */ }
    }, [apiGet]);

    // ── Initial load ──
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await loadConversations();
            await loadUsers();
            await updatePresence();
            await fetchPresence();
            setLoading(false);
        };
        init();

        pollingRef.current = setInterval(() => {
            loadConversations();
            const currentConv = activeConvRef.current;
            if (currentConv) {
                loadMessages(currentConv.conversation_id);
            }
            updatePresence();
            fetchPresence();
        }, POLLING_INTERVAL);

        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [loadConversations, loadUsers, updatePresence, loadMessages, fetchPresence]);

    // ── URL param sync ──
    useEffect(() => {
        const convId = searchParams.get("conversation");
        if (convId && conversations.length > 0) {
            const conv = conversations.find(c => c.conversation_id === convId);
            if (conv && conv.conversation_id !== activeConversation?.conversation_id) {
                setActiveConversation(conv);
                loadMessages(convId);
                setMobileShowChat(true);
            }
        }
    }, [searchParams, conversations, loadMessages, activeConversation?.conversation_id]);

    // ── Scroll to bottom ──
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // ── Helpers ──
    const getConversationName = useCallback((conv) => {
        if (conv.type === "direct") {
            const other = conv.participants?.find(p => p.user_id !== user?.user_id);
            return other?.name || "Conversation";
        }
        return conv.name || "Sans nom";
    }, [user]);

    const selectConversation = (conv) => {
        setActiveConversation(conv);
        setSearchParams({ conversation: conv.conversation_id });
        loadMessages(conv.conversation_id);
        setMobileShowChat(true);
    };

    const goBackToList = () => {
        setMobileShowChat(false);
        setActiveConversation(null);
        setSearchParams({});
    };

    // ── Send message ──
    const sendMessage = async () => {
        if (!newMessage.trim() || !activeConversation || sending) return;
        setSending(true);
        try {
            const msg = await apiPost(`/messaging/conversations/${activeConversation.conversation_id}/messages`, {
                content: newMessage.trim(),
                type: "text",
            });
            setMessages(prev => [...prev, msg]);
            setNewMessage("");
            setConversations(prev =>
                prev.map(c =>
                    c.conversation_id === activeConversation.conversation_id
                        ? { ...c, last_message: msg, updated_at: msg.created_at }
                        : c
                ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
            );
        } catch (err) {
            toast.error(t("messaging.errorSending"));
        } finally {
            setSending(false);
        }
    };

    // ── Start video call (Jitsi) ──
    const startVideoCall = async () => {
        if (!activeConversation || startingCall) return;
        setStartingCall(true);
        try {
            const result = await apiPost(`/messaging/conversations/${activeConversation.conversation_id}/video-call`, {});
            if (result.message) {
                setMessages(prev => [...prev, result.message]);
            }
            setJitsiUrl(result.meeting_link);
            toast.success(t("messaging.videoCallStarted"));
        } catch (err) {
            toast.error(t("messaging.videoCallError"));
        } finally {
            setStartingCall(false);
        }
    };

    const joinVideoCall = (url) => {
        setJitsiUrl(url);
    };

    // ── Upload file ──
    const uploadFile = async (file) => {
        if (!activeConversation || uploading) return;
        if (file.size > 50 * 1024 * 1024) {
            toast.error(t("messaging.fileTooLarge"));
            return;
        }
        setUploading(true);
        setUploadProgress(10);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("caption", newMessage.trim());
            setUploadProgress(30);

            const res = await fetch(
                `${API_URL}/messaging/conversations/${activeConversation.conversation_id}/upload`,
                { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData }
            );
            setUploadProgress(80);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Upload failed");
            }
            const msg = await res.json();
            setMessages(prev => [...prev, msg]);
            setNewMessage("");
            setUploadProgress(100);
            toast.success(t("messaging.fileShared"));
        } catch (err) {
            toast.error(err.message || t("messaging.fileUploadError"));
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
    };

    // ── Drag & Drop handlers ──
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) uploadFile(file);
    };

    const openLightbox = (src, alt) => setLightboxImage({ src, alt });
    const closeLightbox = () => setLightboxImage(null);

    // ── Create group conversation ──
    const createConversation = async () => {
        if (newConvType === "group" && !newConvName.trim()) {
            toast.error(t("messaging.enterGroupName"));
            return;
        }
        if (newConvParticipants.length === 0) {
            toast.error(t("messaging.selectParticipant"));
            return;
        }
        try {
            const conv = await apiPost("/messaging/conversations", {
                name: newConvName.trim() || null,
                type: newConvType,
                description: newConvDesc.trim() || null,
                participant_ids: newConvParticipants,
            });
            setConversations(prev => [conv, ...prev]);
            setActiveConversation(conv);
            setSearchParams({ conversation: conv.conversation_id });
            loadMessages(conv.conversation_id);
            setShowNewDialog(false);
            setNewConvName("");
            setNewConvDesc("");
            setNewConvParticipants([]);
            setMobileShowChat(true);
            toast.success(t("messaging.conversationCreated"));
        } catch (err) {
            toast.error(t("messaging.errorCreating"));
        }
    };

    // ── Group management ──
    const addMember = async (targetUserId) => {
        if (!activeConversation) return;
        try {
            await apiPost(`/messaging/conversations/${activeConversation.conversation_id}/members`, { user_id: targetUserId });
            const updated = await apiGet(`/messaging/conversations/${activeConversation.conversation_id}`);
            setActiveConversation(updated);
            setConversations(prev => prev.map(c => c.conversation_id === updated.conversation_id ? updated : c));
            toast.success(t("messaging.memberAdded"));
        } catch (err) {
            toast.error(err.message || t("messaging.errorAddingMember"));
        }
    };

    const removeMember = async (targetUserId) => {
        if (!activeConversation) return;
        try {
            await request(`/messaging/conversations/${activeConversation.conversation_id}/members/${targetUserId}`, { method: "DELETE" });
            if (targetUserId === user?.user_id) {
                setActiveConversation(null);
                setShowGroupInfo(false);
                fetchConversations();
                return;
            }
            const updated = await apiGet(`/messaging/conversations/${activeConversation.conversation_id}`);
            setActiveConversation(updated);
            setConversations(prev => prev.map(c => c.conversation_id === updated.conversation_id ? updated : c));
            toast.success(t("messaging.memberRemoved"));
        } catch (err) {
            toast.error(err.message || t("messaging.errorRemovingMember"));
        }
    };

    const updateGroupName = async () => {
        if (!activeConversation || !editGroupNameVal.trim()) return;
        try {
            await request(`/messaging/conversations/${activeConversation.conversation_id}`, {
                method: "PUT",
                body: JSON.stringify({ name: editGroupNameVal.trim() }),
            });
            const updated = { ...activeConversation, name: editGroupNameVal.trim() };
            setActiveConversation(updated);
            setConversations(prev => prev.map(c => c.conversation_id === updated.conversation_id ? updated : c));
            setEditingGroupName(false);
            toast.success(t("messaging.groupUpdated"));
        } catch (err) {
            toast.error(t("messaging.errorUpdating"));
        }
    };

    const deleteGroup = async () => {
        if (!activeConversation) return;
        try {
            await request(`/messaging/conversations/${activeConversation.conversation_id}`, { method: "DELETE" });
            setConversations(prev => prev.filter(c => c.conversation_id !== activeConversation.conversation_id));
            setActiveConversation(null);
            setShowGroupInfo(false);
            toast.success(t("messaging.groupDeleted"));
        } catch (err) {
            toast.error(t("messaging.errorDeleting"));
        }
    };

    // ── Start direct chat ──
    const startDirectChat = async (targetUserId) => {
        try {
            const conv = await apiGet(`/messaging/direct/${targetUserId}`);
            if (!conversations.find(c => c.conversation_id === conv.conversation_id)) {
                setConversations(prev => [conv, ...prev]);
            }
            setActiveConversation(conv);
            setSearchParams({ conversation: conv.conversation_id });
            loadMessages(conv.conversation_id);
            setShowNewDialog(false);
            setMobileShowChat(true);
        } catch (err) {
            toast.error(t("messaging.errorCreating"));
        }
    };

    const filteredConversations = conversations.filter(c => {
        if (!searchQuery) return true;
        return getConversationName(c).toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Group conversations by type
    const projectChannels = filteredConversations.filter(c => c.type === "project");
    const directMessages = filteredConversations.filter(c => c.type === "direct");
    const groupChannels = filteredConversations.filter(c => c.type !== "project" && c.type !== "direct");

    const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

    // ── Get presence label for chat header ──
    const getHeaderPresenceInfo = () => {
        if (!activeConversation) return null;
        if (activeConversation.type === "direct") {
            const other = activeConversation.participants?.find(p => p.user_id !== user?.user_id);
            if (other) {
                const status = presenceMap[other.user_id] || "offline";
                const key = `messaging.presence${status.charAt(0).toUpperCase() + status.slice(1)}`;
                return { status, label: t(key) };
            }
        }
        // For group chats, show count of online members
        const onlineCount = (activeConversation.participants || []).filter(
            p => p.user_id !== user?.user_id && presenceMap[p.user_id] === "online"
        ).length;
        if (onlineCount > 0) {
            return { status: "online", label: `${onlineCount} ${t("messaging.presenceOnline").toLowerCase()}` };
        }
        return null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-8rem)]" data-testid="messaging-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const headerPresence = getHeaderPresenceInfo();

    // ── Conversation sidebar panel ──
    const conversationListPanel = (
        <div className={cn(
            "flex flex-col bg-card border-r border-border h-full",
            "w-full md:w-80 md:min-w-[320px]",
            mobileShowChat ? "hidden md:flex" : "flex"
        )} data-testid="conversation-list-panel">
            <div className="p-4 border-b border-border shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-lg font-heading font-bold flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        {t("messaging.title")}
                        {totalUnread > 0 && (
                            <Badge variant="destructive" className="h-5 px-1.5 text-xs">{totalUnread}</Badge>
                        )}
                    </h1>
                    <Button size="icon" variant="ghost" onClick={() => setShowNewDialog(true)} data-testid="new-conversation-btn">
                        <Plus className="w-5 h-5" />
                    </Button>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder={t("messaging.searchPlaceholder")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 h-9"
                        data-testid="conversation-search"
                    />
                </div>
            </div>
            <ScrollArea className="flex-1">
                <div className="p-2 space-y-0.5">
                    {filteredConversations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">{t("messaging.noConversations")}</p>
                            <Button variant="link" size="sm" onClick={() => setShowNewDialog(true)}>
                                {t("messaging.startConversation")}
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* Project Channels */}
                            {projectChannels.length > 0 && (
                                <div className="mb-1">
                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
                                        <FolderKanban className="w-3 h-3" />
                                        {t("messaging.projectChannels")}
                                    </p>
                                    {projectChannels.map(conv => (
                                        <ConversationItem
                                            key={conv.conversation_id}
                                            conv={conv}
                                            isActive={activeConversation?.conversation_id === conv.conversation_id}
                                            unread={unreadCounts[conv.conversation_id] || 0}
                                            userId={user?.user_id}
                                            onClick={() => selectConversation(conv)}
                                            getConversationName={getConversationName}
                                            presenceMap={presenceMap}
                                        />
                                    ))}
                                </div>
                            )}
                            {/* Direct Messages */}
                            {directMessages.length > 0 && (
                                <div className="mb-1">
                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
                                        <User className="w-3 h-3" />
                                        {t("messaging.directMessages")}
                                    </p>
                                    {directMessages.map(conv => (
                                        <ConversationItem
                                            key={conv.conversation_id}
                                            conv={conv}
                                            isActive={activeConversation?.conversation_id === conv.conversation_id}
                                            unread={unreadCounts[conv.conversation_id] || 0}
                                            userId={user?.user_id}
                                            onClick={() => selectConversation(conv)}
                                            getConversationName={getConversationName}
                                            presenceMap={presenceMap}
                                        />
                                    ))}
                                </div>
                            )}
                            {/* Group Channels */}
                            {groupChannels.length > 0 && (
                                <div className="mb-1">
                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
                                        <Hash className="w-3 h-3" />
                                        {t("messaging.groups")}
                                    </p>
                                    {groupChannels.map(conv => (
                                        <ConversationItem
                                            key={conv.conversation_id}
                                            conv={conv}
                                            isActive={activeConversation?.conversation_id === conv.conversation_id}
                                            unread={unreadCounts[conv.conversation_id] || 0}
                                            userId={user?.user_id}
                                            onClick={() => selectConversation(conv)}
                                            getConversationName={getConversationName}
                                            presenceMap={presenceMap}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </ScrollArea>
        </div>
    );

    // ── Chat panel ──
    const chatPanel = activeConversation ? (
        <div className={cn(
            "flex-1 flex flex-col h-full",
            !mobileShowChat ? "hidden md:flex" : "flex"
        )} data-testid="chat-panel">
            {/* Chat Header */}
            <div className="h-14 px-3 md:px-4 border-b border-border flex items-center justify-between bg-card shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <Button variant="ghost" size="icon" className="md:hidden shrink-0" onClick={goBackToList} data-testid="back-to-list-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="relative shrink-0">
                        {activeConversation.type === "direct" ? (
                            <Avatar className="h-9 w-9">
                                <AvatarImage src={activeConversation.participants?.find(p => p.user_id !== user?.user_id)?.avatar} />
                                <AvatarFallback className="text-xs">{getInitials(getConversationName(activeConversation))}</AvatarFallback>
                            </Avatar>
                        ) : (
                            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                                {activeConversation.type === "project" ? <FolderKanban className="w-4 h-4 text-primary" /> :
                                 activeConversation.type === "team" ? <Users className="w-4 h-4 text-primary" /> :
                                 <Hash className="w-4 h-4 text-primary" />}
                            </div>
                        )}
                        {/* Presence dot on header avatar */}
                        {headerPresence && activeConversation.type === "direct" && (
                            <span className="absolute -bottom-0.5 -right-0.5">
                                <PresenceDot status={headerPresence.status} size="sm" />
                            </span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <h2 className="font-semibold text-sm truncate">{getConversationName(activeConversation)}</h2>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                            {headerPresence ? (
                                <>
                                    <span className={cn(
                                        "inline-block w-1.5 h-1.5 rounded-full",
                                        PRESENCE_CONFIG[headerPresence.status]?.color || "bg-gray-400"
                                    )} />
                                    {headerPresence.label}
                                </>
                            ) : (
                                `${activeConversation.participants?.length || 0} ${t("messaging.participants").toLowerCase()}`
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={startVideoCall}
                                    disabled={startingCall}
                                    data-testid="video-call-btn"
                                >
                                    {startingCall ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Video className="w-5 h-5" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("messaging.startVideoCall")}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="shrink-0">
                                <MoreVertical className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {activeConversation?.type !== "direct" && (
                                <DropdownMenuItem onClick={() => { setShowGroupInfo(true); setEditGroupNameVal(activeConversation?.name || ""); }} data-testid="view-group-info-btn">
                                    <Users className="w-4 h-4 mr-2" />
                                    {t("messaging.groupInfo")}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem>
                                <Archive className="w-4 h-4 mr-2" />
                                {t("messaging.archive")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Messages Area */}
            <ScrollArea
                className={cn("flex-1 p-3 md:p-4 relative", isDragging && "ring-2 ring-primary ring-inset")}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {/* Drag overlay */}
                {isDragging && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/5 border-2 border-dashed border-primary rounded-lg pointer-events-none">
                        <div className="text-center">
                            <Paperclip className="w-8 h-8 text-primary mx-auto mb-2" />
                            <p className="text-sm font-medium text-primary">{t("messaging.dropFileHere")}</p>
                        </div>
                    </div>
                )}
                {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                        <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
                        <p>{t("messaging.noMessages")}</p>
                        <p className="text-sm">{t("messaging.startChatting")}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {messages.map((msg, idx) => (
                            <MessageBubble
                                key={msg.message_id}
                                msg={msg}
                                isOwn={msg.sender_id === user?.user_id}
                                isSystem={msg.type === "system"}
                                isMeeting={msg.type === "meeting"}
                                isFile={msg.type === "file"}
                                showAvatar={msg.sender_id !== user?.user_id && (idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id)}
                                token={token}
                                onImageClick={openLightbox}
                                onJoinCall={joinVideoCall}
                                t={t}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </ScrollArea>

            {/* Upload Progress */}
            {uploading && (
                <div className="px-3 md:px-4 shrink-0">
                    <div className="flex items-center gap-2 py-1.5">
                        <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                                className="h-full bg-primary rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                        <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
                    </div>
                </div>
            )}

            {/* Input */}
            <div className="p-3 md:p-4 border-t border-border bg-card shrink-0">
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.mp3,.mp4"
                    data-testid="file-input"
                />
                <div className="flex items-end gap-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 h-[42px] w-[42px]"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="attach-file-btn"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("messaging.attachFile")}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Textarea
                        placeholder={t("messaging.writeMessage")}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        className="min-h-[42px] max-h-28 resize-none text-sm"
                        data-testid="message-input"
                    />
                    <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="shrink-0 h-[42px] w-[42px]"
                        size="icon"
                        data-testid="send-message-btn"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                </div>
            </div>
        </div>
    ) : (
        <div className={cn(
            "flex-1 flex flex-col items-center justify-center text-muted-foreground",
            "hidden md:flex"
        )} data-testid="no-conversation-selected">
            <MessageSquare className="w-16 h-16 mb-4 opacity-30" />
            <h2 className="text-xl font-medium mb-2">{t("messaging.welcome")}</h2>
            <p className="text-sm mb-4">{t("messaging.selectOrCreate")}</p>
            <Button onClick={() => setShowNewDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t("messaging.newConversation")}
            </Button>
        </div>
    );

    return (
        <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-8rem)] flex bg-background rounded-xl border border-border overflow-hidden" data-testid="messaging-page">
            {conversationListPanel}
            {chatPanel}

            {/* Image Lightbox */}
            {lightboxImage && (
                <ImageLightbox
                    src={lightboxImage.src}
                    alt={lightboxImage.alt}
                    onClose={closeLightbox}
                />
            )}

            {/* New Conversation Dialog */}
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t("messaging.newConversation")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2 flex-1 overflow-hidden flex flex-col">
                        <div>
                            <label className="text-sm font-medium mb-1.5 block">{t("messaging.type")}</label>
                            <Select value={newConvType} onValueChange={setNewConvType}>
                                <SelectTrigger data-testid="conv-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            {t("messaging.directMessage")}
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="group">
                                        <div className="flex items-center gap-2">
                                            <Hash className="w-4 h-4" />
                                            {t("messaging.group")}
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {newConvType === "group" && (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium mb-1.5 block">{t("messaging.groupName")}</label>
                                    <Input
                                        placeholder={t("messaging.groupNamePlaceholder")}
                                        value={newConvName}
                                        onChange={(e) => setNewConvName(e.target.value)}
                                        data-testid="conv-name-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1.5 block">{t("messaging.description")}</label>
                                    <Input
                                        placeholder={t("messaging.descriptionPlaceholder")}
                                        value={newConvDesc}
                                        onChange={(e) => setNewConvDesc(e.target.value)}
                                        data-testid="conv-desc-input"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex-1 overflow-hidden flex flex-col">
                            <label className="text-sm font-medium mb-1.5 block">
                                {newConvType === "direct" ? t("messaging.user") : t("messaging.participants")}
                            </label>
                            <ScrollArea className="flex-1 max-h-48">
                                <div className="space-y-1 pr-2">
                                    {users.map((u) => (
                                        <button
                                            key={u.user_id}
                                            onClick={() => {
                                                if (newConvType === "direct") {
                                                    startDirectChat(u.user_id);
                                                } else {
                                                    setNewConvParticipants(prev =>
                                                        prev.includes(u.user_id) ? prev.filter(id => id !== u.user_id) : [...prev, u.user_id]
                                                    );
                                                }
                                            }}
                                            className={cn(
                                                "w-full flex items-center gap-3 p-2 rounded-lg transition-colors",
                                                newConvParticipants.includes(u.user_id) ? "bg-primary/10 text-primary" : "hover:bg-muted"
                                            )}
                                            data-testid={`user-option-${u.user_id}`}
                                        >
                                            <div className="relative">
                                                <Avatar className="h-8 w-8">
                                                    <AvatarImage src={u.picture} />
                                                    <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                                                </Avatar>
                                                <span className="absolute -bottom-0.5 -right-0.5">
                                                    <PresenceDot status={presenceMap[u.user_id] || "offline"} />
                                                </span>
                                            </div>
                                            <div className="flex-1 text-left min-w-0">
                                                <p className="font-medium text-sm truncate">{u.name}</p>
                                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                            </div>
                                            {newConvType === "group" && newConvParticipants.includes(u.user_id) && (
                                                <Check className="w-4 h-4 shrink-0" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                    {newConvType === "group" && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
                                {t("messaging.cancel")}
                            </Button>
                            <Button onClick={createConversation} disabled={newConvParticipants.length === 0} data-testid="create-group-btn">
                                {t("messaging.createGroup")}
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>

            {/* Group Info Slide Panel */}
            {showGroupInfo && activeConversation && activeConversation.type !== "direct" && (() => {
                const isAdmin = activeConversation.participants?.some(p => p.user_id === user?.user_id && p.role === "admin");
                const nonMembers = users.filter(u => !activeConversation.participants?.some(p => p.user_id === u.user_id));
                return (
                    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="group-info-panel">
                        <div className="absolute inset-0 bg-black/40" onClick={() => setShowGroupInfo(false)} />
                        <div className="relative w-full max-w-sm bg-background border-l flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
                            <div className="p-4 border-b flex items-center justify-between">
                                <h3 className="font-semibold text-lg">{t("messaging.groupInfo")}</h3>
                                <Button variant="ghost" size="icon" onClick={() => setShowGroupInfo(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            <ScrollArea className="flex-1">
                                <div className="p-4 space-y-6">
                                    {/* Group Name */}
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("messaging.groupName")}</label>
                                        {editingGroupName ? (
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <Input value={editGroupNameVal} onChange={e => setEditGroupNameVal(e.target.value)} className="flex-1" data-testid="edit-group-name-input" autoFocus />
                                                <Button size="sm" onClick={updateGroupName} data-testid="save-group-name-btn"><Check className="w-4 h-4" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditingGroupName(false)}><X className="w-4 h-4" /></Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between mt-1.5">
                                                <span className="font-medium text-lg">{activeConversation.name}</span>
                                                {isAdmin && (
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditGroupNameVal(activeConversation.name || ""); setEditingGroupName(true); }} data-testid="edit-group-name-btn">
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {activeConversation.description && (
                                            <p className="text-sm text-muted-foreground mt-1">{activeConversation.description}</p>
                                        )}
                                    </div>

                                    {/* Members */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                                {t("messaging.participants")} ({activeConversation.participants?.length || 0})
                                            </label>
                                        </div>
                                        <div className="space-y-1">
                                            {activeConversation.participants?.map(p => (
                                                <div key={p.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50">
                                                    <div className="flex items-center gap-2">
                                                        <Avatar className="h-8 w-8">
                                                            <AvatarImage src={p.avatar} />
                                                            <AvatarFallback className="text-xs">{getInitials(p.name)}</AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <span className="text-sm font-medium">{p.name}</span>
                                                            {p.role === "admin" && (
                                                                <Badge variant="secondary" className="ml-1.5 text-[10px] py-0">
                                                                    <Shield className="w-2.5 h-2.5 mr-0.5" /> Admin
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isAdmin && p.user_id !== user?.user_id && (
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeMember(p.user_id)} data-testid={`remove-member-${p.user_id}`}>
                                                            <UserMinus className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                    {!isAdmin && p.user_id === user?.user_id && (
                                                        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => removeMember(user.user_id)} data-testid="leave-group-btn">
                                                            <LogOut className="w-3.5 h-3.5 mr-1" /> {t("messaging.leaveGroup")}
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Add Members (admin only) */}
                                    {isAdmin && nonMembers.length > 0 && (
                                        <div>
                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">{t("messaging.addMembers")}</label>
                                            <div className="space-y-1 max-h-40 overflow-auto">
                                                {nonMembers.map(u => (
                                                    <button
                                                        key={u.user_id}
                                                        onClick={() => addMember(u.user_id)}
                                                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
                                                        data-testid={`add-member-${u.user_id}`}
                                                    >
                                                        <Avatar className="h-7 w-7">
                                                            <AvatarImage src={u.picture} />
                                                            <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                                                        </Avatar>
                                                        <span className="text-sm">{u.name}</span>
                                                        <UserPlus className="w-3.5 h-3.5 text-primary ml-auto" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Admin: Leave or Delete */}
                                    {isAdmin && (
                                        <div className="pt-2 border-t">
                                            <Button variant="destructive" size="sm" className="w-full" onClick={deleteGroup} data-testid="delete-group-btn">
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                {t("messaging.deleteGroup")}
                                            </Button>
                                        </div>
                                    )}
                                    {!isAdmin && (
                                        <div className="pt-2 border-t">
                                            <Button variant="outline" size="sm" className="w-full text-destructive border-destructive/30" onClick={() => removeMember(user?.user_id)} data-testid="leave-group-btn-bottom">
                                                <LogOut className="w-4 h-4 mr-2" />
                                                {t("messaging.leaveGroup")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                );
            })()}

            {/* Jitsi Video Call Iframe Overlay */}
            {jitsiUrl && (
                <div className="fixed inset-0 z-[70] bg-background/95 backdrop-blur-sm flex flex-col" data-testid="jitsi-iframe-overlay">
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
                        <div className="flex items-center gap-3">
                            <Video className="w-5 h-5 text-primary" />
                            <span className="font-medium">{t("messaging.videoCallInProgress")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(jitsiUrl, "_blank", "noopener,noreferrer")}
                                data-testid="jitsi-open-tab-btn"
                            >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                {t("messaging.openInNewTab")}
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setJitsiUrl(null)}
                                data-testid="jitsi-close-btn"
                            >
                                <X className="w-4 h-4 mr-1" />
                                {t("messaging.endCall")}
                            </Button>
                        </div>
                    </div>
                    <iframe
                        src={jitsiUrl}
                        className="flex-1 w-full border-0"
                        allow="camera;microphone;display-capture;autoplay;clipboard-write"
                        data-testid="jitsi-iframe"
                    />
                </div>
            )}
        </div>
    );
};

export default MessagingPage;
